import { ReactElement, useEffect, useId, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ErrorState, formatDayMonth, GroupsSkeleton, ReportLoading, salesReportIcons, SummarySort } from '../../components/SalesReport'
import { useAuth, useGuard, useHasAccess } from '../../hooks/auth'
import { useEvent } from '../../hooks/events'
import { checkBdApp } from '../../hooks/bdapp/useBdApp'
import {
  exportErrorMessage,
  SalesReportExportFormat,
  useSalesReport,
  useSalesReportExport,
  useSalesReportFilters,
} from '../../hooks/sales-report'
import { useDebounce } from '../../hooks/useDebounce'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import { AccessAndSalesSumaryPageComponent } from '../AcessAndSalesSumary'
import { DEFAULT_FILTERS, periodError, SalesReportFilterState, toSalesReportRequest, withAvailableSelections } from './filters'
import { FiltersPanel } from './FiltersPanel'
import { buildSalesReportView } from './report'
import { ReportContent } from './ReportContent'
import * as S from './styles'

export const NEW_SALES_REPORT_PERMISSION = 'DP25'
export const SALES_REPORT_PERMISSION = 'DP15'
export const STOCK_PERMISSION = 'DP23'
export const FEE_PERMISSION = 'DP24'

const SELECT_DEBOUNCE_MS = 400
const PRINT_TRUNCATED = 'Relatório parcial: exporte em XLSX para ver todas as linhas.'
const PRINT_UNAVAILABLE = 'Carregue o relatório para imprimir.'
const FORMAT_LABELS: Record<SalesReportExportFormat, string> = { csv: 'CSV', xlsx: 'XLSX' }

const reportErrorMessage = (error: any): string | undefined =>
  error?.response?.data?.errorCode === 'REPORT_TOO_LARGE' ? exportErrorMessage('REPORT_TOO_LARGE') : undefined

interface IconPairProps {
  desktop: string
  mobile: string
}

function IconPair({ desktop, mobile }: IconPairProps): ReactElement {
  return (
    <>
      <S.DesktopOnly>
        <img src={desktop} alt="" width={16} height={16} />
      </S.DesktopOnly>
      <S.MobileOnly>
        <img src={mobile} alt="" width={16} height={16} />
      </S.MobileOnly>
    </>
  )
}

export function SalesReport(): ReactElement {
  useGuard([SALES_REPORT_PERMISSION])
  const { id } = useParams()
  const navigate = useNavigate()
  const { authHookWithEvent } = useAuth()
  const { event } = useEvent(Number(id))
  const { filters, isLoading, error, retry } = useSalesReportFilters(Number(id))
  const [filterState, setFilterState] = useState<SalesReportFilterState>(DEFAULT_FILTERS)
  const [period, setPeriod] = useState({ start: DEFAULT_FILTERS.periodStart, end: DEFAULT_FILTERS.periodEnd })
  const [summarySort, setSummarySort] = useState<SummarySort | null>(null)
  const canSeeStock = useHasAccess(STOCK_PERMISSION)
  const canSeeFee = useHasAccess(FEE_PERMISSION)
  const printHintId = useId()
  const native = useMemo(() => checkBdApp().isNative, [])
  const exporter = useSalesReportExport(Number(id))
  const mobile = useMediaQuery('(max-width: 767px)')
  const debouncedFilters = useDebounce(filterState, SELECT_DEBOUNCE_MS)

  const request = useMemo(() => {
    const selected = filters ? withAvailableSelections(debouncedFilters, filters) : debouncedFilters
    const applied = { ...selected, periodStart: period.start, periodEnd: period.end }
    return periodError(applied) === null ? toSalesReportRequest(applied, { canSeeFee }) : null
  }, [debouncedFilters, filters, period, canSeeFee])
  const salesReport = useSalesReport(Number(id), request)
  const { report, error: reportError, retry: retryReport } = salesReport
  const reportLoading = salesReport.isLoading || (Boolean(reportError) && salesReport.isValidating)

  const sectorNames = useMemo(
    () => new Map((filters?.sectors ?? []).map((sector) => [sector.id, sector.name] as [number, string])),
    [filters]
  )
  const view = useMemo(
    () =>
      report === undefined
        ? null
        : buildSalesReportView(report, { showStock: canSeeStock && filterState.showStock, sectorNames }),
    [report, canSeeStock, filterState.showStock, sectorNames]
  )

  const exporting = exporter.state.status === 'running' ? exporter.state.format : null
  const canExport = request !== null && exporting === null
  const exportAs = (format: SalesReportExportFormat): void => {
    if (request !== null) void exporter.start(format, request)
  }
  const showingReport = request !== null && !reportLoading && !reportError && report !== undefined && view !== null
  const printHint = !showingReport ? PRINT_UNAVAILABLE : report.meta.truncated ? PRINT_TRUNCATED : null

  useEffect(() => {
    if (!LOCAL_EXECUTION && id) authHookWithEvent(id, navigate)
  }, [id, authHookWithEvent, navigate])

  const overline = event ? [formatDayMonth(event.data), event.nome].filter(Boolean).join(' — ') : ''

  return (
    <S.Page data-sales-report-print="">
      <S.PrintStyle />
      <S.Header>
        <S.Heading>
          <S.Overline>{overline}</S.Overline>
          <S.Title>Relatório de Vendas</S.Title>
          <S.Description>Acompanhe as vendas do evento com filtros, agrupamentos e exportação.</S.Description>
        </S.Heading>
        <S.Actions data-print-hide="">
          {!native && (
            <>
              {printHint && <S.Hidden id={printHintId}>{printHint}</S.Hidden>}
              <S.ActionButton
                type="button"
                disabled={printHint !== null}
                title={printHint ?? undefined}
                aria-describedby={printHint ? printHintId : undefined}
                onClick={() => window.print()}
              >
                <IconPair desktop={salesReportIcons.printer} mobile={salesReportIcons.printerMobile} />
                Imprimir
              </S.ActionButton>
            </>
          )}
          <S.ActionButton
            type="button"
            disabled={!canExport}
            aria-label="Exportar CSV"
            aria-busy={exporting === 'csv' || undefined}
            onClick={() => exportAs('csv')}
          >
            <IconPair desktop={salesReportIcons.download} mobile={salesReportIcons.downloadMobile} />
            <S.DesktopOnly>Exportar CSV</S.DesktopOnly>
            <S.MobileOnly>CSV</S.MobileOnly>
          </S.ActionButton>
          <S.ActionButton
            type="button"
            $primary
            disabled={!canExport}
            aria-busy={exporting === 'xlsx' || undefined}
            onClick={() => exportAs('xlsx')}
          >
            <IconPair desktop={salesReportIcons.file} mobile={salesReportIcons.downloadLight} />
            Exportar XLSX
          </S.ActionButton>
        </S.Actions>
      </S.Header>
      <S.ExportStatus data-print-hide="" role="status" aria-live="polite">
        {exporting && `Gerando arquivo ${FORMAT_LABELS[exporting]}…`}
      </S.ExportStatus>
      {exporter.state.status === 'error' && (
        <S.ExportError data-print-hide="" role="alert">
          {exporter.state.message}
        </S.ExportError>
      )}
      <div data-print-hide="">
      {filters ? (
        <FiltersPanel
          options={filters}
          value={filterState}
          onChange={setFilterState}
          canSeeStock={canSeeStock && (report === undefined || report.capacity !== undefined)}
          canSeeFee={canSeeFee}
          onPeriodCommit={(start, end) => setPeriod({ start, end })}
        />
      ) : error ? (
        <ErrorState message="Não foi possível carregar os filtros do relatório." onRetry={retry} />
      ) : isLoading ? (
        <GroupsSkeleton groups={1} label="Carregando filtros" />
      ) : null}
      </div>
      {request === null ? null : reportLoading ? (
        <ReportLoading />
      ) : reportError ? (
        <ErrorState message={reportErrorMessage(reportError)} onRetry={retryReport} />
      ) : report === undefined || view === null ? (
        <ReportLoading />
      ) : (
        <ReportContent
          report={report}
          view={view}
          mobile={mobile}
          onExport={() => exportAs('xlsx')}
          exportDisabled={!canExport}
          summarySort={summarySort}
          onSummarySortChange={setSummarySort}
        />
      )}
    </S.Page>
  )
}

export function SalesSummaryRoute(): ReactElement {
  const { id } = useParams()
  const newReport = useHasAccess(NEW_SALES_REPORT_PERMISSION)
  if (newReport) return <SalesReport key={id} />
  return <AccessAndSalesSumaryPageComponent type={'sales'} permissions={[SALES_REPORT_PERMISSION]} />
}
